import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  Plus, 
  MessageSquare, 
  Search, 
  Settings, 
  Archive, 
  Pin,
  Trash2,
  Bot,
  BrainCircuit
} from 'lucide-react';
import { useChatStore } from '../stores/chatStore';
import { cn } from '../utils/api';
import { formatDistanceToNow } from 'date-fns';

export default function Sidebar() {
  const { chats, fetchChats, createChat, currentChat, deleteChat, archiveChat, pinChat } = useChatStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; chatId: string } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  const handleNewChat = async () => {
    const chat = await createChat();
    navigate(`/chat/${chat.id}`);
  };

  const filteredChats = chats.filter(chat => 
    chat.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const pinnedChats = filteredChats.filter(c => c.isPinned);
  const regularChats = filteredChats.filter(c => !c.isPinned);

  const handleContextMenu = (e: React.MouseEvent, chatId: string) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, chatId });
  };

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    window.addEventListener('click', handleClick);
    return () => window.removeEventListener('click', handleClick);
  }, []);

  return (
    <aside className="w-72 bg-legion-card border-r border-legion-border flex flex-col shrink-0">
      {/* Header */}
      <div className="p-4 border-b border-legion-border">
        <button
          onClick={handleNewChat}
          className="w-full flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors font-medium"
        >
          <Plus className="w-5 h-5" />
          New chat
        </button>
      </div>

      {/* Search */}
      <div className="px-4 py-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-legion-dark border border-legion-border rounded-lg text-sm focus:outline-none focus:border-indigo-500 transition-colors"
          />
        </div>
      </div>

      {/* Navigation */}
      <nav className="px-2 pb-2">
        <NavLink
          to="/research"
          className={({ isActive }) => cn(
            "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm",
            isActive ? "bg-indigo-600/20 text-indigo-400" : "hover:bg-white/5 text-gray-300"
          )}
        >
          <BrainCircuit className="w-4 h-4" />
          Deep Research
        </NavLink>
        <NavLink
          to="/settings"
          className={({ isActive }) => cn(
            "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm",
            isActive ? "bg-indigo-600/20 text-indigo-400" : "hover:bg-white/5 text-gray-300"
          )}
        >
          <Settings className="w-4 h-4" />
          Settings
        </NavLink>
      </nav>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto px-2">
        {pinnedChats.length > 0 && (
          <div className="mb-2">
            <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Pinned
            </div>
            {pinnedChats.map(chat => (
              <ChatItem
                key={chat.id}
                chat={chat}
                isActive={currentChat?.id === chat.id}
                onContextMenu={handleContextMenu}
              />
            ))}
          </div>
        )}

        <div>
          <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wider">
            Recent
          </div>
          {regularChats.map(chat => (
            <ChatItem
              key={chat.id}
              chat={chat}
              isActive={currentChat?.id === chat.id}
              onContextMenu={handleContextMenu}
            />
          ))}
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-legion-card border border-legion-border rounded-lg shadow-xl py-1 z-50 min-w-[160px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            onClick={() => {
              pinChat(contextMenu.chatId, !chats.find(c => c.id === contextMenu.chatId)?.isPinned);
              setContextMenu(null);
            }}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-white/10 transition-colors"
          >
            <Pin className="w-4 h-4" />
            {chats.find(c => c.id === contextMenu.chatId)?.isPinned ? 'Unpin' : 'Pin'}
          </button>
          <button
            onClick={() => {
              archiveChat(contextMenu.chatId);
              setContextMenu(null);
            }}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-white/10 transition-colors"
          >
            <Archive className="w-4 h-4" />
            Archive
          </button>
          <div className="border-t border-legion-border my-1" />
          <button
            onClick={() => {
              deleteChat(contextMenu.chatId);
              setContextMenu(null);
            }}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-white/10 transition-colors text-red-400"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}

      {/* Footer */}
      <div className="p-4 border-t border-legion-border">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Bot className="w-4 h-4" />
          <span>DEATH LEGION V1</span>
        </div>
      </div>
    </aside>
  );
}

interface ChatItemProps {
  chat: { id: string; title: string; updatedAt: string; lastMessage?: string };
  isActive: boolean;
  onContextMenu: (e: React.MouseEvent, chatId: string) => void;
}

function ChatItem({ chat, isActive, onContextMenu }: ChatItemProps) {
  return (
    <NavLink
      to={`/chat/${chat.id}`}
      onContextMenu={(e) => onContextMenu(e, chat.id)}
      className={cn(
        "flex items-start gap-3 px-3 py-2 rounded-lg transition-colors group",
        isActive ? "bg-indigo-600/20 text-white" : "hover:bg-white/5 text-gray-300"
      )}
    >
      <MessageSquare className="w-4 h-4 mt-0.5 shrink-0 text-gray-500" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{chat.title}</p>
        <p className="text-xs text-gray-500 truncate">
          {chat.lastMessage || (chat.updatedAt && !isNaN(new Date(chat.updatedAt).getTime()) ? formatDistanceToNow(new Date(chat.updatedAt), { addSuffix: true }) : 'Just now')}
        </p>
      </div>
    </NavLink>
  );
}
